import React, { useEffect, useState } from "react";
import { ChartDisplayProps } from "@shared/interfaces/Charts"; // Importing the interface for chart-related props
import ArkEditor from "@shared/components/EditorJs/ArkEditor"; // Custom Editor component for rich text editing
import { OutputData } from "@editorjs/editorjs";
import { displayReportName } from "@pages/Project/utils/utils";

// Interface defining the expected props for the ReportEditor component
interface ReportEditorProps {
  initialContent: string; // Initial content to populate the editor
  initialTitle: string; // Initial title for the report
  setEditorContent: (content: string, title?: string) => void; // Function to update the editor's content
  setEditorTitle: (title: string) => void; // Function to update the editor's title
  plotsData: ChartDisplayProps; // Data related to chart displays, passed to the editor
  created_at: Date;
  template_name: string; // Optional template name for the report
}

const ReportEditor: React.FC<ReportEditorProps> = ({
  initialContent,
  initialTitle,
  setEditorContent,
  setEditorTitle,
  plotsData,
  created_at,
  template_name,
}) => {
  // State to store and manage the report's title locally within the component
  const [title, setTitle] = useState("");

  useEffect(() => {
    setTitle(displayReportName(initialTitle, template_name, created_at));
    setEditorTitle(displayReportName(initialTitle, template_name, created_at));
  }, []);

  /**
   * Handles changes in the report's title.
   * Updates both the local component state and informs the parent component of the change.
   * @param e - The input change event containing the new title value.
   */
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value); // Update local state
    setEditorTitle(e.target.value); // Notify parent of the title change
  };

  /**
   * Handles saving the editor's content by converting the raw Editor.js data to HTML.
   * Updates canvas IDs within the HTML to ensure they are unique before passing the content to the parent component.
   * @param data - The raw data from the editor.
   */

  const handleSave = (data: OutputData) => {
    setEditorContent(JSON.stringify(data, null, 2)); // Pass the processed content to the parent component
  };

  return (
    <div className="mx-auto p-7 flex flex-col justify-center">
      {/* Input field for setting the report's title */}
      <div className="w-full max-w-[calc(100%-60px)] mx-auto">
        <input
          type="text"
          value={title}
          onChange={handleTitleChange}
          placeholder="Enter title"
          className="w-full p-2 text-2xl 2xl:text-3xl font-bold mb-7 border border-gray-300 rounded "
        />
      </div>

      {/* Custom rich text editor for report content */}
      <ArkEditor
        initialContent={initialContent} // Initial content for the editor
        plotsData={plotsData} // Chart data to be displayed within the editor
        onSave={handleSave} // Save handler for processing and storing content
      />
    </div>
  );
};

export default ReportEditor; // Exporting the ReportEditor component as the default export
